import { Autocomplete, Box, Button, Divider, FormControl, FormControlLabel, FormLabel, Grid, IconButton, Paper, Radio, RadioGroup, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { isEmpty, isNumberEmpty, OfferType, printDebugLog, RESYMBOL, StayType } from "../../constants/Constants";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from '@mui/icons-material/Delete';


const RoomForm = ({
    stayType,
    totalDaysStay,
    roomData,
    totalHotelRoomDetails,
    setTotalHotelRoomDetails,
    billPayInfo,
    setBillPayInfo,
    setSnpComponent, disabledOperation }) => {

    const [isEdit, setIsEdit] = useState(false);
    const [columnData, setColumnData] = useState(null);
    const [selData, setSelData] = useState(null);
    const canSelectRate = stayType !== -1 && totalDaysStay > 0;
    const showExtraBed =
        columnData?.extraBedQuan > 0 &&
        columnData?.extraBedPrice > 0 &&
        columnData?.sAmt > 0;

    useEffect(() => {
        printDebugLog("RoomForm billPayInfo", billPayInfo);
    }, [billPayInfo, totalHotelRoomDetails]);


    function upsertRoomItem(itemData) {
        if (!itemData || itemData.id == null) {
            console.warn("upsertRoomItem: itemData.id is required");
            return;
        }

        setTotalHotelRoomDetails(prev => {
            const items = [...prev.items];
            const idx = items.findIndex(it => String(it.id) === String(itemData.id));

            if (idx >= 0) {
                items[idx] = { ...items[idx], ...itemData };
                return {
                    ...prev,
                    items,
                    added: items.length > 0,
                };
            } else {
                items.push(itemData);
                return {
                    ...prev,
                    items,
                    totalSize: prev.totalSize + 1,
                    added: true,
                };
            }
        });
    }

    function deleteRoomItem(id) {
        setTotalHotelRoomDetails(prev => {
            const nextItems = prev.items.filter(it => String(it.id) !== String(id));
            const removed = nextItems.length !== prev.items.length;

            return {
                ...prev,
                items: nextItems,
                totalSize: removed ? Math.max(0, prev.totalSize - 1) : prev.totalSize,
                added: nextItems.length > 0 ? prev.added : false,
            };
        });
    }

    const doTotal = (mTotalRoomPrice) => {
        printDebugLog("doTotal", mTotalRoomPrice, billPayInfo);
        let mCgstAmt = 0.0;
        let mSgstAmt = 0.0;
        let mRoundOff = 0.0;
        let mSubTotal = 0.0;
        let mFinalPrice = 0.0;
        const parseCgst = Number(billPayInfo?.cgst ?? 0) / 100;
        const parseSgst = Number(billPayInfo?.sgst ?? 0) / 100;
        const parseOfferDenom = Number(billPayInfo?.takenDenom ?? OfferType.No_Offer);
        const parseOfferAmt = Number(billPayInfo?.takenOffrAmt ?? 0);

        const restoNExtraTotalPrice = Number(billPayInfo?.totalExtraPrice ?? 0) + Number(billPayInfo?.totalRestaurantBillPrice ?? 0);

        mSubTotal = mTotalRoomPrice + Number(billPayInfo?.totalBanquetPrice ?? 0);

        mSubTotal = (mSubTotal * totalDaysStay) + restoNExtraTotalPrice;
        mFinalPrice = mSubTotal;
        if (parseOfferDenom === OfferType.Flat) {
            mFinalPrice -= parseOfferAmt;
        } else if (parseOfferDenom === OfferType.Percentage) {
            mFinalPrice -= (mFinalPrice * parseOfferAmt / 100);
        }
        if (billPayInfo?.gstAllowed ?? true) {
            mCgstAmt = (mFinalPrice * parseCgst);
            mSgstAmt = (mFinalPrice * parseSgst);
        }
        mFinalPrice = mFinalPrice + mCgstAmt + mSgstAmt;
        mRoundOff = mFinalPrice.toFixed() - mFinalPrice;
        mFinalPrice = mFinalPrice + mRoundOff;
        const pendingAmt = billPayInfo?.paymentInitiated ?? false ? billPayInfo?.pendingAmt ?? 0 : mFinalPrice;
        setBillPayInfo(prev => ({
            ...prev,
            totalRoomPrice: mTotalRoomPrice,
            cgstAmt: mCgstAmt,
            sgstAmt: mSgstAmt,
            roundOff: mRoundOff,
            totalPrice: mSubTotal,
            finalPrice: mFinalPrice,
            pendingAmt: pendingAmt,
        }));
    }

    const updateData = () => {
        printDebugLog("updateData", columnData);
        if (columnData) {
            let oldPrice = 0.0;
            let extraOldPrice = 0.0;
            const oldIndex = totalHotelRoomDetails.items.findIndex(item => item.id === columnData.id);
            if (oldIndex !== -1) {
                oldPrice = totalHotelRoomDetails.items[oldIndex].sAmt || 0;
                extraOldPrice = totalHotelRoomDetails.items[oldIndex].extraBedPrice || 0;
            }
            const mTotalExtraOldPrice = Number(columnData?.extraBedFullPrice ?? 0) - extraOldPrice;
            const mTotalRoomPrice = Number(billPayInfo?.totalRoomPrice ?? 0) + Number(columnData?.sAmt ?? 0) - oldPrice + mTotalExtraOldPrice;

            doTotal(mTotalRoomPrice);
            upsertRoomItem(columnData);

            setColumnData(null);
            setSelData(null);
            setSnpComponent(prev => ({
                ...prev,
                snackBarOpen: true,
                snackBarMsg: `${isEdit ? "Updated" : "Created"} Successfully`,
            }))
            setIsEdit(false);
        } else {
            setSnpComponent(prev => ({
                ...prev,
                snackBarOpen: true,
                snackBarMsg: "Please select rate card",
            }))
        }
    }

    const deleteCurrentRoom = (columnData) => {
        if (disabledOperation) {
            setSnpComponent(prev => ({
                ...prev,
                snackBarOpen: true,
                snackBarMsg: "Cancelled bill cannot be deleted",
            }));
            return;
        }
        if (billPayInfo?.paymentInitiated ?? false) {
            setSnpComponent(prev => ({
                ...prev,
                snackBarOpen: true,
                snackBarMsg: "Paid bill cannot be deleted",
            }));
            return;
        }
        printDebugLog("deleteCurrentRoom", columnData);
        const mTotalExtraOldPrice = Number(billPayInfo?.totalExtraPrice ?? 0) - Number(columnData?.extraBedFullPrice ?? 0);
        const mTotalRoomPrice = Number(billPayInfo?.totalRoomPrice ?? 0) - Number(columnData?.sAmt ?? 0) + mTotalExtraOldPrice;

        doTotal(mTotalRoomPrice);
        deleteRoomItem(columnData?.id);
        setColumnData(null);
        setSelData(null);
        setSnpComponent(prev => ({
            ...prev,
            snackBarOpen: true,
            snackBarMsg: `Deleted Successfully`,
        }))
    }

    const handleEditRow = (r) => {
        printDebugLog("EditIcon,getColums ", r);
        if (disabledOperation) {
            setSnpComponent(prev => ({
                ...prev,
                snackBarOpen: true,
                snackBarMsg: "Cancelled bill cannot be deleted",
            }));
            return;
        }
        if (billPayInfo?.paymentInitiated ?? false) {
            setSnpComponent(prev => ({
                ...prev,
                snackBarOpen: true,
                snackBarMsg: "Paid bill cannot be edited",
            }));
            return;
        }
        const item = roomData?.find(x => String(x.id) === String(r.id));
        if (item) {
            setColumnData(prev => ({
                ...prev,
                ...r
            }));
            setSelData(prev => ({
                ...prev,
                ...item,
            }));
            setIsEdit(true);
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper
                    elevation={4}
                    autoHeight
                    sx={{
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "#fbfffe",
                    }}>

                    <Grid container spacing={2}>
                        {
                            !isEdit && <Grid item xs={12} md={4} sm={6}>
                                <Autocomplete
                                    id="room-select"
                                    options={roomData}
                                    autoHighlight
                                    variant="filled"
                                    size="small"
                                    disabled={disabledOperation}
                                    fullWidth
                                    onChange={(e, option) => {
                                        if (!canSelectRate) {
                                            setSnpComponent(prev => ({ ...prev, snackBarOpen: true, snackBarMsg: "Please save the Date Selection Form" }));
                                            return;
                                        }
                                        if (option) {
                                            const matchedBookedItem = totalHotelRoomDetails?.items?.find(booked =>
                                                option.priceList?.some(
                                                    price =>
                                                        String(booked.rateId) === String(price.id) &&
                                                        String(booked.id) === String(price.prdId)
                                                )
                                            );
                                            if (matchedBookedItem) {
                                                setColumnData(prev => ({
                                                    ...prev,
                                                    ...matchedBookedItem,
                                                }));
                                                setIsEdit(true);
                                            }
                                            setSelData(prev => ({
                                                ...prev,
                                                ...option,
                                            }));
                                        } else {
                                            setColumnData(null);
                                            setSelData(null);
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(option) => option?.name ?? ""}
                                    value={selData ?? null}
                                    renderOption={(props, option) => {
                                        const isSelected = totalHotelRoomDetails?.items?.some(
                                            (item) => String(item.id) === String(option.roomId)
                                        );

                                        return (
                                            <Box
                                                component="li"
                                                {...props}
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-start",
                                                    padding: "6px 12px",
                                                    backgroundColor: isSelected ? "#f9f9f9" : "transparent",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    "&:hover": {
                                                        backgroundColor: "#f0f0f0",
                                                    },
                                                }}
                                            >
                                                {/* Option roomType */}
                                                <span
                                                    style={{
                                                        color: isSelected ? "grey" : "#000",
                                                        fontWeight: isSelected ? "500" : "600",
                                                        fontSize: "14px",
                                                    }}
                                                >
                                                    {`${option.name || ""}`}
                                                </span>

                                                {/* Show "Already Selected" if matched */}
                                                {isSelected && (
                                                    <span
                                                        style={{
                                                            color: "#1976d2", // MUI blue
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        Already Selected
                                                    </span>
                                                )}
                                            </Box>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Room"
                                            inputProps={{
                                                ...params.inputProps,
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        }
                        {
                            selData &&
                            <Grid item xs={12}>
                                <p
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "break-word",
                                        fontFamily: "Arial",
                                        padding: "2px",
                                        margin: 0
                                    }}
                                >
                                    <span
                                        style={{
                                            color: "black",
                                            fontWeight: "bold",
                                            fontSize: "14px"
                                        }}
                                    >
                                        Selected Room:{" "}
                                        <span
                                            style={{
                                                color: "#00333e",
                                                fontWeight: "bold",
                                                fontSize: "16px"
                                            }}
                                        >
                                            {selData?.name || ""}
                                        </span>
                                    </span>

                                    <span
                                        style={{
                                            color: "gray",
                                            fontSize: "14px",
                                            marginTop: "2px"
                                        }}
                                    >
                                        Category:{" "}
                                        <span
                                            style={{
                                                fontSize: "14px"
                                            }}
                                        >
                                            {selData?.catgName || ""}
                                        </span>
                                    </span>
                                </p>

                            </Grid>
                        }

                        {
                            (selData && selData?.priceList) && <Grid item xs={12}>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Rate Card</FormLabel>
                                    <RadioGroup aria-label="rate-select" name="rate-select" value={columnData?.rateId ?? ""} onChange={(e) => {
                                        if (!canSelectRate) {
                                            setSnpComponent(prev => ({ ...prev, snackBarOpen: true, snackBarMsg: "Please save the Date Selection Form" }));
                                            return;
                                        }

                                        const item = selData?.priceList?.find(x => String(x.id) === String(e.target.value));
                                        if (!item) return;

                                        const parseAmt = Number(e.target.id) || 0;
                                        setColumnData(prev => ({
                                            ...prev,
                                            rateId: e.target.value,
                                            sAmt: parseAmt,
                                            rateName: e.target.name,
                                            catgName: selData?.catgName || "",
                                            roomName: selData?.name || "",
                                            roomNo: selData?.roomNo || "",
                                            tAmt: parseAmt * totalDaysStay,
                                            id: item.prdId,
                                            type: Number(item.type),
                                        }));

                                    }}>
                                        {selData?.priceList.map((data) => (
                                            <div>
                                                <FormControlLabel control={
                                                    <Radio
                                                        value={data.id}
                                                        name={data.name}
                                                        id={stayType === StayType.DAILY ? data.sAmt : data.hourlyAmt}
                                                    />}
                                                    label={`${data.name ?? ""} Price: ${RESYMBOL} ${stayType === StayType.DAILY ? data.sAmt : data.hourlyAmt} / ${stayType === StayType.DAILY ? "day" : "hour"}`} />
                                            </div>
                                        ))}
                                    </RadioGroup>
                                </FormControl>

                            </Grid>
                        }
                        {
                            (columnData && !isEmpty(columnData?.rateId)) && <Grid item xs={12} md={6} sm={6}>
                                <TextField
                                    id="extraBedQuan"
                                    label="Enter Quantity"
                                    type="number"
                                    name="extraBedQuan"
                                    value={columnData?.extraBedQuan ?? ""}
                                    variant="filled"
                                    size="small"
                                    helperText="Quantity for Extra Bed"
                                    onChange={(e) => {
                                        if (!isNumberEmpty(e.target.value)) {
                                            const val = Number(e.target.value);
                                            const eFullPrice = (val * Number(columnData?.extraBedPrice ?? 0));
                                            const roomTprice = (Number(columnData?.sAmt ?? 0) + eFullPrice);
                                            const tPrice = roomTprice * totalDaysStay;
                                            setColumnData(prev => ({
                                                ...prev, extraBedQuan: val, tAmt: tPrice, extraBedFullPrice: eFullPrice, rooTAmt: roomTprice,
                                            }))
                                        } else {
                                            const eFullPrice = (0 * Number(columnData?.extraBedPrice ?? 0));
                                            const roomTprice = (Number(columnData?.sAmt ?? 0) + eFullPrice);
                                            const tPrice = roomTprice * totalDaysStay;
                                            setColumnData(prev => ({
                                                ...prev, extraBedQuan: "", tAmt: tPrice, extraBedFullPrice: eFullPrice, rooTAmt: roomTprice,
                                            }))
                                        }
                                    }}
                                    fullWidth
                                    inputProps={{ min: '0' }}
                                />
                            </Grid>
                        }
                        {
                            (columnData && !isEmpty(columnData?.rateId)) && <Grid item xs={12} md={6} sm={6}>
                                <TextField
                                    id="extraBedPrice"
                                    label="Enter Price"
                                    type="number"
                                    name="extraBedPrice"
                                    value={columnData?.extraBedPrice ?? ""}
                                    variant="filled"
                                    size="small"
                                    helperText="Price for Extra Bed/per quan"
                                    onChange={(e) => {
                                        if (!isNumberEmpty(e.target.value)) {
                                            const val = Number(e.target.value);
                                            const eFullPrice = Number(columnData?.extraBedQuan ?? 0) * val;
                                            const roomTprice = (Number(columnData?.sAmt ?? 0) + eFullPrice);
                                            const tPrice = roomTprice * totalDaysStay;
                                            setColumnData(prev => ({
                                                ...prev, extraBedPrice: val, tAmt: tPrice, extraBedFullPrice: eFullPrice, rooTAmt: roomTprice,
                                            }))
                                        } else {

                                            const tPrice = Number(columnData?.sAmt ?? 0) * totalDaysStay;
                                            setColumnData(prev => ({
                                                ...prev, extraBedPrice: "", tAmt: tPrice, extraBedFullPrice: 0, rooTAmt: Number(columnData?.sAmt ?? 0),
                                            }))
                                        }
                                    }}
                                    fullWidth
                                    inputProps={{ min: '0' }}
                                />
                            </Grid>
                        }
                        {
                            (columnData && !isEmpty(columnData?.rateId) && columnData?.sAmt > 0) && <Grid item xs={12} md={9} sm={9}>
                                <Typography variant="body1" style={{ fontSize: "19px", color: "#2f1160" }}>
                                    Current amount :{" "}
                                    {`${totalDaysStay} ${stayType === StayType.DAILY ? "days" : "hours"} x `}
                                    {showExtraBed
                                        ? `Room: ₹ ${columnData?.sAmt} + (Extra: ${columnData?.extraBedQuan} x ₹ ${columnData?.extraBedPrice}) = `
                                        : `${columnData?.sAmt} = `}
                                    ₹ {columnData?.tAmt ?? 0}
                                </Typography>

                            </Grid>
                        }

                    </Grid>

                </Paper>
            </Grid>
            {
                selData &&
                <Grid item xs={12} md={6} sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {
                        isEdit && <Button
                            size="small"
                            variant="outlined"
                            onPointerDown={() => {
                                setColumnData(null);
                                setSelData(null);
                                setIsEdit(false);
                            }}
                        >
                            Cancel
                        </Button>
                    }
                    {
                        isEdit && <Button
                            size="small"
                            variant="outlined"
                            disabled={disabledOperation}
                            onPointerDown={() => deleteCurrentRoom()}
                        >
                            Delete
                        </Button>
                    }
                    <Button
                        size="small"
                        variant="contained"
                        disabled={disabledOperation}
                        onPointerDown={() => {
                            updateData();
                        }}
                    >
                        Update
                    </Button>
                </Grid>
            }
            <Grid item xs={12}>
                <TableContainer component={Paper} elevation={3} sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                    border: "none",
                    borderRadius: 2,
                }}
                >
                    <Table aria-label="extra-orders-table">
                        <TableHead>
                            <TableRow sx={{
                                backgroundColor: "#01333e",
                                "& .MuiTableCell-root": {
                                    color: "#fff",
                                    fontWeight: "bold",
                                    fontSize: "0.8rem",
                                    padding: "6px 8px",
                                    borderBottom: "none",
                                },
                            }}>
                                <TableCell align="center">Details</TableCell>
                                <TableCell align="center">ExtraBed Quan.</TableCell>
                                <TableCell align="center">{`Price/${stayType === StayType.DAILY ? "day" : "hour"}`}</TableCell>
                                <TableCell align="center">T.Price</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {totalHotelRoomDetails?.items.length === 0 ? (
                                <TableRow>
                                    <TableCell align="center" colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">
                                            No items yet. Add some above.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                totalHotelRoomDetails?.items.map((r) => (
                                    <TableRow key={`${r.id}::${r.rateId}`}>
                                        <TableCell align="center">{`${r.roomName || ""} ${isEmpty(r.rateName) ? "" : ` (${r.rateName})`}`}</TableCell>
                                        <TableCell align="center">{r.extraBedQuan}</TableCell>
                                        <TableCell align="center">{`${RESYMBOL} ${r.sAmt}`}</TableCell>
                                        <TableCell align="center">{`${RESYMBOL} ${r.tAmt}`}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton
                                                    size="small"
                                                    aria-label="edit"
                                                    onPointerDown={() => handleEditRow(r)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label="delete"
                                                    onPointerDown={() => deleteCurrentRoom(r)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
};
export { RoomForm };